// src/pages/SDSRegister.tsx — G-05: Safety Data Sheet / Hazardous Chemicals Register
// Per-job SDS register accessible on site. WHS Regulation 2011 (Qld) Part 7.1.
// Chemical inventory, emergency contacts, PPE matrix, spill procedures.

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_SDS, SDSEntry, getWHSDocumentStyle } from '../utils/mockDataWHSMgmt'
import { MOCK_JOBS } from '../utils/mockData'
import { AlertTriangle, Plus, Search, X, ChevronRight, Download, Shield, Phone, CheckCircle, Sparkles } from 'lucide-react'

function fmtDate(s: string) { return new Date(s).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'2-digit'}) }
function sdsIsExpired(entry: SDSEntry) {
  const rev = new Date(entry.sdsRevisionDate)
  const yearsOld = (Date.now() - rev.getTime()) / (1000 * 60 * 60 * 24 * 365)
  return yearsOld > 5   // SDS must be <5 years old (Safe Work Australia)
}

function SDSDetail({ sds, onClose }: { sds: SDSEntry; onClose: ()=>void }) {
  const expired = sdsIsExpired(sds)
  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className={`px-5 py-4 border-b ${expired?'bg-red-50':'bg-gray-50'} flex-shrink-0`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-gray-400">{sds.productCode}</span>
              {expired?<span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">SDS EXPIRED — REPLACE</span>:<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">SDS current</span>}
            </div>
            <div className="font-semibold text-gray-900">{sds.chemicalName}</div>
            <div className="text-xs text-gray-500 mt-0.5">{sds.supplier} · SDS revision: {fmtDate(sds.sdsRevisionDate)}</div>
          </div>
          <div className="flex gap-2"><button className="btn-secondary text-xs flex items-center gap-1"><Download className="w-3.5 h-3.5"/>SDS PDF</button><button onClick={onClose}><X className="w-4 h-4 text-gray-400"/></button></div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {/* Emergency contacts */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <div className="text-xs font-semibold text-red-900 mb-2 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5"/>Emergency contacts</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[['Emergency services', '000'],['Poisons Information Centre','13 11 26'],['WorkSafe QLD (24hr)','1300 362 128']].map(([l,p])=>(
              <div key={l} className="flex items-center justify-between bg-white rounded-lg px-2.5 py-1.5 border border-red-200"><span className="text-red-800 font-medium">{l}</span><span className="font-mono font-bold text-red-700">{p}</span></div>
            ))}
          </div>
        </div>

        {/* Hazard classification */}
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-1.5">GHS hazard classification</div>
          <div className="flex flex-wrap gap-1.5">{sds.hazardClassification.map(h=><span key={h} className="text-xs bg-orange-100 text-orange-800 border border-orange-200 px-2 py-0.5 rounded-full font-medium">{h}</span>)}</div>
        </div>

        {sds.casNumber && <div><div className="text-xs text-gray-500 mb-0.5">CAS number</div><div className="font-mono text-sm text-gray-900">{sds.casNumber}</div></div>}

        {/* PPE */}
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-1.5">Required PPE</div>
          <div className="flex flex-wrap gap-1.5">{sds.ppeRequired.map(p=><span key={p} className="text-xs bg-blue-100 text-blue-800 border border-blue-200 px-2 py-0.5 rounded-full">🦺 {p}</span>)}</div>
        </div>

        {/* First aid */}
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700">First aid procedures</div>
          <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-xs text-gray-700">
            <div><strong>Inhalation:</strong> {sds.firstAidInhalation}</div>
            <div><strong>Skin/eye contact:</strong> {sds.firstAidContact}</div>
          </div>
        </div>

        {/* Spill response */}
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-1">Spill / emergency response</div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">{sds.spillResponse}</div>
        </div>

        {/* Storage */}
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-1">Storage requirements</div>
          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-700">{sds.storageRequirements}</div>
        </div>

        <div><div className="text-xs text-gray-500 mb-0.5">Maximum quantity permitted on site</div><div className="font-medium text-sm text-gray-900">{sds.maxQtyOnSite}</div></div>
        <div><div className="text-xs text-gray-500 mb-0.5">Used on job sites</div><div className="flex gap-1.5 flex-wrap">{sds.jobIds.map(jId=>{const job = MOCK_JOBS.find(j=>j.id===jId); return <span key={jId} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono">{job?.jobNumber??jId}</span>})}</div></div>
      </div>
    </div>
  )
}

export function SDSRegister() {
  const { isTier } = useAuthStore()
  const canEdit = isTier('B_PC') || isTier('B_PM') || isTier('A_DEVELOPER')
  const [entries, setEntries] = useState(MOCK_SDS)
  const [selected, setSelected] = useState<SDSEntry|null>(null)
  const [search, setSearch]     = useState('')
  const [jobFilter, setJobFilter] = useState('ALL')
  const [showNew, setShowNew]   = useState(false)
  const style = getWHSDocumentStyle()

  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))
  const filtered = entries.filter(e =>
    (jobFilter==='ALL'||e.jobIds.includes(jobFilter)) &&
    (!search||e.chemicalName.toLowerCase().includes(search.toLowerCase())||e.productCode.toLowerCase().includes(search.toLowerCase()))
  )
  const expiredCount = entries.filter(sdsIsExpired).length

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Shield className="w-6 h-6 text-orange-500"/>SDS & Hazardous Chemicals Register</h1>
          <p className="text-sm text-gray-500 mt-0.5">Safety data sheets · chemical inventory · emergency procedures · WHS Reg 2011 Part 7.1</p>
        </div>
        {canEdit && <button onClick={()=>setShowNew(true)} className="btn-primary text-sm flex items-center gap-2"><Plus className="w-4 h-4"/>Add chemical</button>}
      </div>

      {expiredCount > 0 && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800"><AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/><span><strong>{expiredCount} SDS document{expiredCount!==1?'s':''} expired</strong> (&gt;5 years old). Workers must have access to current SDS. Replace immediately.</span></div>}

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
        <div className="font-semibold mb-1">WHS Regulation 2011 (Qld) s.346 — SDS access requirements</div>
        A current SDS must be readily accessible to workers who use, handle, or store a hazardous chemical. SDS must be in English. SDS must be no more than 5 years old. This register must be available on site during works.
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[{ label:'Chemicals on register', value:entries.length, color:'text-gray-900', sub:'all job sites' },{ label:'SDS current', value:entries.filter(e=>!sdsIsExpired(e)).length, color:'text-green-700', sub:'within 5 years' },{ label:'SDS expired / action required', value:expiredCount, color:expiredCount>0?'text-red-600':'text-gray-400', sub:'replace immediately if >0' }].map(({label,value,color,sub})=>(
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3"><div className={`text-2xl font-bold ${color}`}>{value}</div><div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div><div className="text-xs text-gray-400">{sub}</div></div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-40"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"/><input className="input pl-9 text-sm" placeholder="Search chemicals…" value={search} onChange={e=>setSearch(e.target.value)}/></div>
        <select className="input text-sm w-auto" value={jobFilter} onChange={e=>setJobFilter(e.target.value)}><option value="ALL">All job sites</option>{activeJobs.map(j=><option key={j.id} value={j.id}>{j.jobNumber}</option>)}</select>
        <button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Print site register</button>
      </div>

      {showNew && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between"><h4 className="font-semibold text-blue-900">Add chemical to SDS register</h4><button onClick={()=>setShowNew(false)}><X className="w-4 h-4 text-gray-400"/></button></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Chemical / product name <span className="text-red-500">*</span></label><input className="input text-sm" placeholder="e.g. Penetrating Oil WD-40"/></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Product code</label><input className="input text-sm"/></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Supplier</label><input className="input text-sm"/></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">SDS revision date <span className="text-red-500">*</span></label><input type="date" className="input text-sm"/></div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800"><AlertTriangle className="w-3 h-3 inline mr-1"/>Upload the current SDS document and complete all first aid and emergency response fields before saving. Incomplete SDS records do not satisfy WHS Regulation requirements.</div>
          <div className="flex justify-end gap-2"><button onClick={()=>setShowNew(false)} className="btn-secondary text-sm">Cancel</button><button onClick={()=>setShowNew(false)} className="btn-primary text-sm">Save chemical</button></div>
        </div>
      )}

      <div className="flex gap-5 items-start">
        <div className={`${selected?'w-1/2':'w-full'} space-y-2 transition-all`}>
          {filtered.map(sds => {
            const expired = sdsIsExpired(sds); const isSel = selected?.id===sds.id
            return (
              <div key={sds.id} onClick={()=>setSelected(isSel?null:sds)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel?'border-blue-400 ring-2 ring-blue-100':expired?'border-red-300 bg-red-50/30':'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{sds.productCode}</span>
                      {expired?<span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">SDS EXPIRED</span>:<span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">✓ Current</span>}
                    </div>
                    <div className="font-semibold text-gray-900">{sds.chemicalName}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex gap-3 flex-wrap"><span>{sds.supplier}</span><span>SDS: {fmtDate(sds.sdsRevisionDate)}</span><span>{sds.hazardClassification[0]}</span></div>
                    <div className="flex gap-1 mt-1.5">{sds.ppeRequired.slice(0,3).map(p=><span key={p} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">{p}</span>)}</div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel?'text-blue-500':'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
          {filtered.length===0&&<div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">No chemicals in register for this site</div>}
        </div>
        {selected && <div className="w-1/2 sticky top-6" style={{maxHeight:'calc(100vh - 160px)'}}><SDSDetail sds={selected} onClose={()=>setSelected(null)}/></div>}
      </div>
    </div>
  )
}
